/* ==========================================================================
   JS ESTÁTICO: combo-local.js
   Optimizado para Shopify + Mobile + LCP / INP / CLS

   Características:
   - Event delegation para reducir listeners.
   - Galería sin listeners individuales por thumbnail.
   - Cambio de imagen con decode() cuando está disponible.
   - Configurador inicializado al cargar la sección.
   - requestAnimationFrame para actualizaciones visuales.
   - Compatible con Shopify Theme Editor / section:load.
   - No interfiere con EasySell.
   - Sin dependencias externas.
   ========================================================================== */

(() => {
  'use strict';

  const GLOBAL_KEY = '__muuvaComboLocal';
  const existingInstance = window[GLOBAL_KEY];

  if (existingInstance?.initAllSections) {
    existingInstance.initAllSections(document);
    return;
  }

  const SECTION_SELECTOR = '.muuva-hero-wrapper[data-section-id]';
  const initializedSections = new WeakSet();

  /**
   * ------------------------------------------------------------------------
   * Utilidades
   * ------------------------------------------------------------------------
   */

  const getSectionId = (wrapper) => wrapper?.dataset?.sectionId || '';

  const query = (root, selector) => root?.querySelector(selector) || null;

  const queryAll = (root, selector) => (
    root ? Array.from(root.querySelectorAll(selector)) : []
  );

  const raf = (callback) => {
    if ('requestAnimationFrame' in window) {
      window.requestAnimationFrame(callback);
    } else {
      setTimeout(callback, 0);
    }
  };

  const validColor = (value, fallback) => (
    /^#[0-9a-f]{3,8}$/i.test(value || '') ? value : fallback
  );

  const validNumber = (value, fallback) => {
    const number = Number.parseFloat(value);

    return Number.isFinite(number) ? number : fallback;
  };

  const setStyleProperty = (element, property, value) => {
    element.style.setProperty(property, value);
  };

  const initStyles = (wrapper) => {
    const section = wrapper.closest('.shopify-section-muuva-hero') || wrapper;
    const { dataset } = wrapper;

    setStyleProperty(
      section,
      '--muuva-bg',
      validColor(dataset.muuvaBg, '#1A1A1A')
    );
    setStyleProperty(
      section,
      '--muuva-card-bg',
      validColor(dataset.muuvaCardBg, '#2C2C2C')
    );
    setStyleProperty(
      section,
      '--muuva-gift-bg',
      validColor(dataset.muuvaGiftBg, '#2A1616')
    );
    setStyleProperty(
      section,
      '--muuva-gift-border',
      validColor(dataset.muuvaGiftBorder, '#E30613')
    );

    setStyleProperty(
      wrapper,
      '--pt',
      `${validNumber(dataset.muuvaPaddingTop, 48)}px`
    );
    setStyleProperty(
      wrapper,
      '--pb',
      `${validNumber(dataset.muuvaPaddingBottom, 48)}px`
    );
    setStyleProperty(
      wrapper,
      '--pt-m',
      `${validNumber(dataset.muuvaPaddingTopMobile, 32)}px`
    );
    setStyleProperty(
      wrapper,
      '--pb-m',
      `${validNumber(dataset.muuvaPaddingBottomMobile, 32)}px`
    );
    setStyleProperty(
      wrapper,
      '--max-w',
      `${validNumber(dataset.muuvaMaxWidth, 1200)}px`
    );
    setStyleProperty(
      wrapper,
      '--container-px',
      `${validNumber(dataset.muuvaContainerPadding, 15)}px`
    );
    setStyleProperty(
      wrapper,
      '--container-px-m',
      `${validNumber(dataset.muuvaContainerPaddingMobile, 12)}px`
    );
    setStyleProperty(
      wrapper,
      '--badge-fs-d',
      `${validNumber(dataset.muuvaBadgeFontSize, 13)}px`
    );
    setStyleProperty(
      wrapper,
      '--badge-fs-m',
      `${validNumber(dataset.muuvaBadgeFontSizeMobile, 11)}px`
    );
    setStyleProperty(
      wrapper,
      '--muuva-main-image-ratio',
      String(validNumber(dataset.muuvaMainImageRatio, 1))
    );
    setStyleProperty(
      wrapper,
      '--muuva-main-image-fit',
      dataset.muuvaMainImageFit === 'cover' ? 'cover' : 'contain'
    );

    const borderWidth = validNumber(
      dataset.muuvaMainBorderWidth,
      2
    );
    const borderColor = validColor(
      dataset.muuvaMainBorderColor,
      '#FFC107'
    );
    const border = dataset.muuvaMainBorderEnabled === 'true'
      ? `${borderWidth}px solid ${borderColor}`
      : '0px solid transparent';

    setStyleProperty(wrapper, '--muuva-main-border', border);
    setStyleProperty(
      wrapper,
      '--muuva-main-border-radius',
      `${validNumber(dataset.muuvaMainBorderRadius, 0)}px`
    );
    setStyleProperty(
      wrapper,
      '--muuva-gift-padding',
      `${validNumber(dataset.muuvaGiftPadding, 6)}px`
    );
    setStyleProperty(
      wrapper,
      '--muuva-gift-font-size',
      `${validNumber(dataset.muuvaGiftFontSize, 12)}px`
    );
  };

  /**
   * ------------------------------------------------------------------------
   * GALERÍA
   * ------------------------------------------------------------------------
   */

  const initGallery = (wrapper) => {
    const sectionId = getSectionId(wrapper);

    if (!sectionId) return;

    const mainImg = query(
      wrapper,
      `#muuva-main-img-${CSS.escape(sectionId)}`
    );

    const mainPicture = query(
      wrapper,
      `#muuva-main-picture-${CSS.escape(sectionId)}`
    );

    if (!mainImg) return;

    /**
     * Actualiza únicamente las fuentes necesarias.
     */
    const updatePictureSources = (button) => {
      if (!mainPicture) return;

      const sources = queryAll(mainPicture, 'source');

      const mobileWebp = button.dataset.mobileWebp || '';
      const fullWebp = button.dataset.fullWebp || '';

      if (sources.length >= 2) {
        if (mobileWebp) {
          sources[0].srcset = mobileWebp;
        }

        if (fullWebp) {
          sources[1].srcset = fullWebp;
        }
      } else if (sources.length === 1 && fullWebp) {
        sources[0].srcset = fullWebp;
      }
    };

    /**
     * Cambia la imagen principal.
     *
     * Se utiliza decode() cuando está disponible para evitar
     * mostrar una imagen parcialmente decodificada.
     */
    let imageRequestId = 0;

    const changeMainImage = (button) => {
      const newSrc = button.dataset.fullSrc || '';

      if (!newSrc) return;

      const requestId = ++imageRequestId;

      updatePictureSources(button);

      const preloadImage = new Image();

      preloadImage.decoding = 'async';
      preloadImage.src = newSrc;

      const applyImage = () => {
        if (requestId !== imageRequestId) return;

        if (mainImg.src !== newSrc) {
          mainImg.src = newSrc;
        }
      };

      if (typeof preloadImage.decode === 'function') {
        preloadImage.decode()
          .then(applyImage)
          .catch(applyImage);
      } else {
        preloadImage.onload = applyImage;
        preloadImage.onerror = applyImage;
      }
    };

    /**
     * Event delegation:
     * un solo listener para todas las thumbnails.
     */
    wrapper.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.muuva-hero-thumb-btn');

        if (!button || !wrapper.contains(button)) {
          return;
        }

        const thumbnails = queryAll(
          wrapper,
          '.muuva-hero-thumb-btn'
        );

        thumbnails.forEach((thumb) => {
          thumb.classList.toggle(
            'is-active',
            thumb === button
          );
        });

        changeMainImage(button);
      },
      { passive: true }
    );

    const mediaMain = query(
      wrapper,
      '.muuva-hero-media-main'
    );

    if (!mediaMain) return;

    let touchStart = null;

    mediaMain.addEventListener(
      'touchstart',
      (event) => {
        if (
          !window.matchMedia('(max-width: 767px)').matches ||
          event.touches.length !== 1
        ) {
          touchStart = null;
          return;
        }

        const touch = event.touches[0];

        touchStart = {
          x: touch.clientX,
          y: touch.clientY
        };
      },
      { passive: true }
    );

    mediaMain.addEventListener(
      'touchend',
      (event) => {
        if (
          !touchStart ||
          !window.matchMedia('(max-width: 767px)').matches
        ) {
          return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStart.x;
        const deltaY = touch.clientY - touchStart.y;

        touchStart = null;

        if (
          Math.abs(deltaX) < 40 ||
          Math.abs(deltaX) <= Math.abs(deltaY)
        ) {
          return;
        }

        const thumbnails = queryAll(
          wrapper,
          '.muuva-hero-thumb-btn'
        );

        if (thumbnails.length < 2) return;

        const activeIndex = thumbnails.findIndex((thumb) => (
          thumb.classList.contains('is-active')
        ));
        const currentIndex = activeIndex >= 0 ? activeIndex : 0;
        const nextIndex = currentIndex + (deltaX < 0 ? 1 : -1);

        if (
          nextIndex < 0 ||
          nextIndex >= thumbnails.length
        ) {
          return;
        }

        thumbnails[nextIndex].click();
      },
      { passive: true }
    );
  };

  /**
   * ------------------------------------------------------------------------
   * CONFIGURADOR DEL COMBO
   * ------------------------------------------------------------------------
   */

  const initBuilder = (wrapper) => {
    const sectionId = getSectionId(wrapper);

    if (!sectionId) return;

    const builderBox = query(
      wrapper,
      '.muuva-pack-builder-box'
    );

    if (!builderBox) return;

    const chips = queryAll(
      builderBox,
      '.muuva-butter-chip'
    );

    const fatRadios = queryAll(
      builderBox,
      '.muuva-fat-radio'
    );

    const totalElement = query(
      builderBox,
      `#muuva-butter-total-${CSS.escape(sectionId)}`
    );

    const submitButton = query(
      wrapper,
      `#muuva-submit-btn-${CSS.escape(sectionId)}`
    );

    const summaryButters = queryAll(
      builderBox,
      '.muuva-summary-butter'
    );

    const customizeButton = query(
      builderBox,
      '[data-builder-toggle]'
    );

    const butterFieldset = query(
      builderBox,
      '.muuva-builder-fieldset--butters'
    );

    const butterProperties = [];

    for (let i = 1; i <= 3; i += 1) {
      butterProperties.push(
        query(
          wrapper,
          `#prop-butter-${i}-${CSS.escape(sectionId)}`
        )
      );
    }

    const fatProperty = query(
      wrapper,
      `#prop-fat-${CSS.escape(sectionId)}`
    );

    const counts = new Array(chips.length).fill(1);

    const MAX_BUTTER = 3;

    /**
     * Experiencia #4: traduce los contadores a la variante correcta.
     * Las 10 variantes tienen SKU AAA…ACP (A=Ajo, P=Pimentón, C=Canela).
     */
    const FLAVOR_CODE = {
      'Pimentón': 'P',
      'Ajo': 'A',
      'Canela': 'C',
    };

    const buildSku = () => chips
      .map((chip, index) => {
        const name = Object.keys(FLAVOR_CODE).find((key) => (
          (chip.dataset.butterVal || '').startsWith(key)
        ));

        return name ? FLAVOR_CODE[name].repeat(counts[index]) : '';
      })
      .join('')
      .split('')
      .sort()
      .join('');

    const applyVariant = () => {
      const product = window.ES_INITIAL_PRODUCT;
      const variants = (product && product.variants) || [];
      const sku = buildSku();
      const match = variants.find((variant) => (
        String(variant.sku || '').toUpperCase() === sku
      ));

      if (!match) return;

      const variantId = String(match.id);

      document
        .querySelectorAll('form[action*="/cart/add"] input[name="id"]')
        .forEach((input) => {
          if (input.value !== variantId) {
            input.value = variantId;
          }
        });
    };

    let updateQueued = false;

    const getTotal = () => (
      counts.reduce(
        (total, value) => total + value,
        0
      )
    );

    const syncCustomizeButton = () => {
      if (!customizeButton || !butterFieldset) return;

      customizeButton.disabled = (
        !butterFieldset.hidden &&
        getTotal() !== MAX_BUTTER
      );
    };

    const hasFatSelected = () => (
      fatRadios.some((radio) => radio.checked)
    );

    /**
     * Actualización visual agrupada en un único frame.
     */
    const updateBuilder = () => {
      if (updateQueued) return;

      updateQueued = true;

      raf(() => {
        updateQueued = false;

        const total = getTotal();

        syncCustomizeButton();

        /**
         * Actualizamos únicamente los nodos cuyo estado pueda cambiar.
         */
        chips.forEach((chip, index) => {
          const count = counts[index];

          const countElement = query(
            chip,
            '.muuva-butter-count'
          );

          const minusButton = query(
            chip,
            '.muuva-butter-minus'
          );

          const plusButton = query(
            chip,
            '.muuva-butter-plus'
          );

          const selected = count > 0;

          if (
            countElement &&
            countElement.textContent !== String(count)
          ) {
            countElement.textContent = String(count);
          }

          chip.classList.toggle(
            'is-selected',
            selected
          );

          if (countElement) {
            countElement.style.display = selected
              ? 'flex'
              : '';
          }

          if (minusButton) {
            minusButton.disabled = count <= 0;
          }

          if (plusButton) {
            plusButton.disabled = (
              total >= MAX_BUTTER ||
              count >= MAX_BUTTER
            );
          }
        });

        summaryButters.forEach((summaryButter, index) => {
          const count = counts[index] || 0;
          const countElement = query(
            summaryButter,
            '[data-summary-butter-count]'
          );

          summaryButter.hidden = count === 0;

          if (countElement) {
            const text = count > 1 ? `×${count}` : '';

            countElement.hidden = count <= 1;

            if (countElement.textContent !== text) {
              countElement.textContent = text;
            }
          }
        });

        /**
         * Texto contador.
         */
        if (totalElement) {
          const text = total === MAX_BUTTER
            ? '3/3 ✓'
            : `${MAX_BUTTER - total} por elegir (${total}/3)`;

          if (totalElement.textContent !== text) {
            totalElement.textContent = text;
          }
        }

        /**
         * Propiedades del producto.
         */
        const selectedButterters = [];

        chips.forEach((chip, index) => {
          const value = chip.dataset.butterVal || '';
          const count = counts[index];

          for (let i = 0; i < count; i += 1) {
            selectedButterters.push(value);
          }
        });

        butterProperties.forEach((input, index) => {
          if (!input) return;

          const value = selectedButterters[index] || '';

          if (input.value !== value) {
            input.value = value;
          }
        });

        /**
         * Variante del producto según los contadores.
         */
        applyVariant();

        /**
         * Estado del botón nativo.
         *
         * IMPORTANTE:
         * EasySell sigue funcionando independientemente de esto.
         * Si EasySell está activo, el botón nativo se oculta mediante CSS.
         */
        const ready = total === MAX_BUTTER;

        if (submitButton) {
          submitButton.style.opacity = ready ? '1' : '0.5';
          submitButton.style.pointerEvents = ready
            ? 'auto'
            : 'none';
        }
      });
    };

    if (customizeButton && butterFieldset) {
      const openText = customizeButton.dataset.openText
        || customizeButton.textContent.trim();
      const closeText = customizeButton.dataset.closeText || openText;

      const setCustomizationState = (isOpen) => {
        butterFieldset.hidden = !isOpen;
        syncCustomizeButton();
        customizeButton.setAttribute(
          'aria-expanded',
          String(isOpen)
        );
        customizeButton.textContent = isOpen
          ? closeText
          : openText;
      };

      customizeButton.addEventListener(
        'click',
        () => setCustomizationState(butterFieldset.hidden)
      );

      setCustomizationState(!butterFieldset.hidden);
    }

    /**
     * Event delegation del configurador.
     *
     * Un listener para +/- en lugar de un listener por botón.
     */
    builderBox.addEventListener(
      'click',
      (event) => {
        const plusButton = event.target.closest(
          '.muuva-butter-plus'
        );

        const minusButton = event.target.closest(
          '.muuva-butter-minus'
        );

        if (!plusButton && !minusButton) {
          return;
        }

        const chip = event.target.closest(
          '.muuva-butter-chip'
        );

        if (!chip || !builderBox.contains(chip)) {
          return;
        }

        const index = chips.indexOf(chip);

        if (index === -1) {
          return;
        }

        event.preventDefault();

        if (plusButton) {
          const total = getTotal();

          if (
            total < MAX_BUTTER &&
            counts[index] < MAX_BUTTER
          ) {
            counts[index] += 1;
            updateBuilder();
          }

          return;
        }

        if (
          minusButton &&
          counts[index] > 0
        ) {
          counts[index] -= 1;
          updateBuilder();
        }
      },
      { passive: false }
    );

    /**
     * Un único listener change para todos los radios.
     */
    builderBox.addEventListener(
      'change',
      (event) => {
        const radio = event.target.closest(
          '.muuva-fat-radio'
        );

        if (!radio || !builderBox.contains(radio)) {
          return;
        }

        if (fatProperty) {
          fatProperty.value = radio.value || '';
        }

        updateBuilder();
      },
      { passive: true }
    );

    /**
     * Estado inicial.
     */
    updateBuilder();
  };

  /**
   * ------------------------------------------------------------------------
   * INICIALIZACIÓN DE CADA SECCIÓN
   * ------------------------------------------------------------------------
   */

  const initSection = (wrapper) => {
    if (!wrapper || initializedSections.has(wrapper)) {
      return;
    }

    initializedSections.add(wrapper);

    initStyles(wrapper);
    initGallery(wrapper);

    initBuilder(wrapper);
  };

  /**
   * ------------------------------------------------------------------------
   * TABS DE TRANSFORMACIÓN (combo-local_transformation.liquid)
   * ------------------------------------------------------------------------
   */

  const initTransformTabs = (wrapper) => {
    if (!wrapper || initializedSections.has(wrapper)) {
      return;
    }

    if (wrapper.dataset.tabsEnabled !== 'true') {
      return;
    }

    const tabBtns = queryAll(
      wrapper,
      '.muuva-transform-tab-btn'
    );

    const cards = queryAll(
      wrapper,
      '.muuva-transform-card'
    );

    if (!tabBtns.length || !cards.length) {
      return;
    }

    initializedSections.add(wrapper);

    let resizeTimer;

    const updateTabs = () => {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const activeBtn = query(
        wrapper,
        '.muuva-transform-tab-btn.is-active'
      ) || tabBtns[0];
      const activeIndex = activeBtn.dataset.tabIndex;

      cards.forEach((card) => {
        const shouldShow = !isMobile || card.dataset.cardIndex === activeIndex;

        card.classList.toggle('is-tab-hidden', !shouldShow);
      });
    };

    const setupTabs = () => {
      tabBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          tabBtns.forEach((b) => {
            b.classList.remove('is-active');
            b.setAttribute('aria-selected', 'false');
          });

          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');

          updateTabs();
        });
      });

      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(updateTabs, 150);
      });

      updateTabs();
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setupTabs();
            obs.disconnect();
          }
        });
      }, { rootMargin: '100px' });

      observer.observe(wrapper);

      return;
    }

    setupTabs();
  };

  const initTransformSections = (root = document) => {
    const wrappers = queryAll(
      root,
      '.muuva-transform-wrapper[data-section-id]'
    );

    wrappers.forEach(initTransformTabs);
  };

  /**
   * ------------------------------------------------------------------------
   * GRID DE INTERÉS / DESGLOSE DE PRODUCTOS (combo-local_interes.liquid)
   * Filtrado por categoría, Touch Reveal, Scroll Sync, Flechas y GSAP Guard
   * ------------------------------------------------------------------------
   */

  const initPgrid = (wrapper) => {
    if (!wrapper || initializedSections.has(wrapper)) {
      return;
    }

    initializedSections.add(wrapper);

    const filterBtns = queryAll(
      wrapper,
      '.muuva-pgrid-filter-btn'
    );

    const cards = queryAll(
      wrapper,
      '.muuva-pgrid-card[data-category]'
    );

    const containerGrid = query(
      wrapper,
      '.muuva-pgrid-container-grid'
    );

    const dots = queryAll(
      wrapper,
      '.muuva-pgrid-dot'
    );

    const prevArrow = query(
      wrapper,
      '.muuva-pgrid-nav-arrow.prev'
    );

    const nextArrow = query(
      wrapper,
      '.muuva-pgrid-nav-arrow.next'
    );

    // 1. FILTRADO POR CATEGORÍA DATA-DRIVEN
    if (filterBtns.length && cards.length) {
      filterBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const filterValue = btn.dataset.filter;

          filterBtns.forEach((b) => {
            const isActive = b === btn;

            b.classList.toggle('is-active', isActive);
            b.setAttribute('aria-pressed', String(isActive));
          });

          cards.forEach((card) => {
            const cardCat = card.dataset.category;
            const shouldShow = filterValue === 'all' || cardCat === filterValue;

            card.classList.toggle('is-hidden', !shouldShow);
          });

          dots.forEach((dot, idx) => {
            const card = cards[idx];

            dot.classList.toggle(
              'is-hidden',
              !card || card.classList.contains('is-hidden')
            );
          });

          if (containerGrid) {
            containerGrid.scrollLeft = 0;
          }

          setActiveDot(0);
        });
      });
    }

    // 2. MICRO-INTERACCIÓN REVELAR DETALLE (TAP OVERLAY TOGGLE)
    cards.forEach((card) => {
      const trigger = query(
        card,
        '.muuva-pgrid-reveal-trigger'
      );

      const closeBtn = query(
        card,
        '.muuva-pgrid-close-btn'
      );

      const overlay = query(
        card,
        '.muuva-pgrid-card-overlay'
      );

      if (trigger && overlay) {
        trigger.addEventListener('click', (event) => {
          event.stopPropagation();
          card.classList.add('is-active-overlay');
          overlay.setAttribute('aria-hidden', 'false');
        });
      }

      if (closeBtn && overlay) {
        closeBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          card.classList.remove('is-active-overlay');
          overlay.setAttribute('aria-hidden', 'true');
        });
      }
    });

    const getCardWidth = () => {
      const firstVisible = queryAll(
        containerGrid,
        '.muuva-pgrid-card'
      ).find((card) => !card.classList.contains('is-hidden'));

      return firstVisible?.offsetWidth || 300;
    };

    const setActiveDot = (scrollLeft) => {
      const visibleCards = cards.filter(
        (card) => !card.classList.contains('is-hidden')
      );

      if (!dots.length || !visibleCards.length) return;

      const activeIndex = Math.min(
        visibleCards.length - 1,
        Math.max(0, Math.round(scrollLeft / getCardWidth()))
      );

      const activeCard = visibleCards[activeIndex];
      const activeDot = dots[cards.indexOf(activeCard)];

      dots.forEach((dot) => dot.classList.remove('is-active'));

      if (activeDot) {
        activeDot.classList.add('is-active');
      }
    };

    // 3. PAGINACIÓN DOTS EN CARRUSEL MOBILE
    if (containerGrid && dots.length) {
      containerGrid.addEventListener(
        'scroll',
        () => {
          setActiveDot(containerGrid.scrollLeft);
        },
        { passive: true }
      );
    }

    // 4. FLECHAS DE NAVEGACIÓN EN MÓVIL
    if (containerGrid) {
      if (prevArrow) {
        prevArrow.addEventListener('click', () => {
          containerGrid.scrollBy({
            left: -getCardWidth(),
            behavior: 'smooth'
          });
        });
      }

      if (nextArrow) {
        nextArrow.addEventListener('click', () => {
          containerGrid.scrollBy({
            left: getCardWidth(),
            behavior: 'smooth'
          });
        });
      }
    }

    // 5. GUARD CLAUSE Y ANIMACIÓN GSAP (OPT-IN)
    if (wrapper.dataset.animate === 'true') {
      if (typeof gsap === 'undefined') {
        console.warn('[MUUVA] GSAP no detectado en theme.liquid. Omitiendo animación.');
        return;
      }

      const duration = Number.parseFloat(wrapper.dataset.duration) || 0.6;
      const ease = wrapper.dataset.ease || 'power2.out';
      const animType = wrapper.dataset.animationType;

      const mm = gsap.matchMedia();

      mm.add(
        {
          reduceMotion: '(prefers-reduced-motion: reduce)',
          all: '(min-width: 1px)'
        },
        (context) => {
          if (context.conditions.reduceMotion) return;

          const items = queryAll(
            wrapper,
            '.animate-item'
          );

          if (!items.length) return;

          const scrollTrigger = {
            trigger: wrapper,
            start: 'top 85%'
          };

          if (animType === 'fade-up') {
            gsap.from(items, {
              y: 30,
              opacity: 0,
              duration: duration,
              ease: ease,
              stagger: 0.1,
              scrollTrigger: scrollTrigger
            });
          } else if (animType === 'fade-in') {
            gsap.from(items, {
              opacity: 0,
              duration: duration,
              ease: ease,
              stagger: 0.1,
              scrollTrigger: scrollTrigger
            });
          } else if (animType === 'stagger-scale') {
            gsap.from(items, {
              scale: 0.92,
              opacity: 0,
              duration: duration,
              ease: ease,
              stagger: 0.12,
              scrollTrigger: scrollTrigger
            });
          }
        }
      );

      wrapper.addEventListener('shopify:section:unload', () => {
        if (typeof mm !== 'undefined') {
          mm.revert();
        }
      });
    }
  };

  const initPgridSections = (root = document) => {
    const wrappers = queryAll(
      root,
      '.muuva-pgrid-wrapper[data-section-id]'
    );

    wrappers.forEach(initPgrid);
  };

  /**
   * ------------------------------------------------------------------------
   * CARRUSEL DE DESEO / PRUEBA SOCIAL (combo-local_deseo.liquid)
   * Flechas + Dots Sync, GSAP Guard
   * ------------------------------------------------------------------------
   */

  const initProof = (wrapper) => {
    if (!wrapper || initializedSections.has(wrapper)) {
      return;
    }

    initializedSections.add(wrapper);

    const grid = query(
      wrapper,
      '.muuva-proof-grid'
    );

    const prevBtn = query(
      wrapper,
      '.muuva-proof-prev-btn'
    );

    const nextBtn = query(
      wrapper,
      '.muuva-proof-next-btn'
    );

    const dots = queryAll(
      wrapper,
      '.muuva-proof-dot'
    );

    if (!grid) return;

    const updateControls = () => {
      const scrollLeft = grid.scrollLeft;
      const maxScroll = grid.scrollWidth - grid.clientWidth;

      if (prevBtn) prevBtn.disabled = scrollLeft <= 5;
      if (nextBtn) nextBtn.disabled = scrollLeft >= maxScroll - 5;

      // Sincronizar puntos de navegación activa
      if (dots.length > 0) {
        const card = query(
          grid,
          '.muuva-proof-card'
        );
        const cardWidth = card?.offsetWidth || grid.clientWidth;
        const activeIndex = Math.round(scrollLeft / cardWidth);

        dots.forEach((dot, index) => {
          if (index === activeIndex) {
            dot.classList.add('is-active');
            dot.setAttribute('aria-selected', 'true');
          } else {
            dot.classList.remove('is-active');
            dot.setAttribute('aria-selected', 'false');
          }
        });
      }
    };

    const getStep = () => {
      const card = query(
        grid,
        '.muuva-proof-card'
      );

      return card ? card.offsetWidth + 12 : 300;
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        grid.scrollBy({ left: -getStep(), behavior: 'smooth' });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        grid.scrollBy({ left: getStep(), behavior: 'smooth' });
      });
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.slideIndex, 10);
        grid.scrollTo({ left: idx * getStep(), behavior: 'smooth' });
      });
    });

    grid.addEventListener('scroll', updateControls, { passive: true });
    updateControls();

    // GUARD CLAUSE Y ANIMACIÓN GSAP (OPT-IN)
    if (wrapper.dataset.animate === 'true') {
      if (typeof gsap === 'undefined') {
        console.warn('[MUUVA] GSAP no detectado en theme.liquid. Omitiendo animación.');
        return;
      }

      const duration = Number.parseFloat(wrapper.dataset.duration) || 0.6;
      const ease = wrapper.dataset.ease || 'power2.out';
      const animType = wrapper.dataset.animationType;

      const mm = gsap.matchMedia();

      mm.add(
        {
          reduceMotion: '(prefers-reduced-motion: reduce)',
          all: '(min-width: 1px)'
        },
        (context) => {
          if (context.conditions.reduceMotion) return;

          const items = queryAll(
            wrapper,
            '.animate-item'
          );

          if (!items.length) return;

          const scrollTrigger = {
            trigger: wrapper,
            start: 'top 85%'
          };

          if (animType === 'fade-up') {
            gsap.from(items, {
              y: 30,
              opacity: 0,
              duration: duration,
              ease: ease,
              stagger: 0.1,
              scrollTrigger: scrollTrigger
            });
          } else if (animType === 'fade-in') {
            gsap.from(items, {
              opacity: 0,
              duration: duration,
              ease: ease,
              stagger: 0.1,
              scrollTrigger: scrollTrigger
            });
          } else if (animType === 'stagger-scale') {
            gsap.from(items, {
              scale: 0.92,
              opacity: 0,
              duration: duration,
              ease: ease,
              stagger: 0.12,
              scrollTrigger: scrollTrigger
            });
          }
        }
      );

      wrapper.addEventListener('shopify:section:unload', () => {
        if (typeof mm !== 'undefined') {
          mm.revert();
        }
      });
    }
  };

  const initProofSections = (root = document) => {
    const wrappers = queryAll(
      root,
      '.muuva-proof-wrapper[data-section-id]'
    );

    wrappers.forEach(initProof);
  };

  // ===== SECCIÓN: combo-local_comparacion.liquid =====
  /* ==========================================================================
     SECCIÓN COMPARACIÓN (combo-local_comparacion.liquid) — Modo E (2.8)
     Referencia: Comparation-Section_v1.txt (muuva-vs-others v6.8.1)
     Módulos: initCompare (acordeón móvil exclusivo + GSAP opt-in) | initCompareSections
     ========================================================================== */

  const initCompare = (wrapper) => {
    if (!wrapper || initializedSections.has(wrapper)) {
      return;
    }

    initializedSections.add(wrapper);

    const toggleBtns = queryAll(wrapper, '.muuva-vs-toggle-btn');

    if (!toggleBtns.length) return;

    const openText = wrapper.dataset.detailsOpen || '+ Ver detalles';
    const closeText = wrapper.dataset.detailsClose || '- Ocultar';

    toggleBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const rowBlock = btn.closest('.muuva-vs-row-block');
        if (!rowBlock) return;

        const isCurrentlyExpanded = btn.getAttribute('aria-expanded') === 'true';

        // CERRAR TODAS LAS DEMÁS FILAS PARA EVITAR DEFORMACIONES Y MANTENER APERTURA ÚNICA
        toggleBtns.forEach((otherBtn) => {
          if (otherBtn !== btn) {
            const otherRow = otherBtn.closest('.muuva-vs-row-block');
            if (otherRow) {
              const otherWrappers = queryAll(otherRow, '.muuva-vs-cell-text-wrapper');
              otherWrappers.forEach((w) => w.classList.remove('is-expanded'));
              otherBtn.setAttribute('aria-expanded', 'false');
              const span = otherBtn.querySelector('span');
              if (span) span.textContent = openText;
            }
          }
        });

        // TOGGLE EN LA FILA SELECCIONADA CON TRANSICIÓN SUAVE
        const currentTextWrappers = queryAll(rowBlock, '.muuva-vs-cell-text-wrapper');

        if (isCurrentlyExpanded) {
          currentTextWrappers.forEach((w) => w.classList.remove('is-expanded'));
          btn.setAttribute('aria-expanded', 'false');
          const span = btn.querySelector('span');
          if (span) span.textContent = openText;
        } else {
          currentTextWrappers.forEach((w) => w.classList.add('is-expanded'));
          btn.setAttribute('aria-expanded', 'true');
          const span = btn.querySelector('span');
          if (span) span.textContent = closeText;
        }
      });
    });

    /* GSAP opt-in (5.13): entrada scoped con guard. CSS nativo si no hay GSAP. */
    if (wrapper.dataset.animate !== 'true' || typeof gsap === 'undefined') {
      return;
    }

    const duration = parseFloat(wrapper.dataset.duration) || 0.6;
    const ease = wrapper.dataset.ease || 'power2.out';
    const animationType = wrapper.dataset.animationType || 'fade-up';
    const items = queryAll(wrapper, '.muuva-vs-head-group, .muuva-vs-matrix-card');

    if (!items.length) return;

    const mm = gsap.matchMedia();

    mm.add(
      {
        reduceMotion: '(prefers-reduced-motion: reduce)'
      },
      (context) => {
        if (context.conditions.reduceMotion) return;

        const from = {
          opacity: 0,
          duration: duration,
          ease: ease
        };

        if (animationType === 'fade-up') {
          from.y = 24;
        }

        gsap.from(items, from);
      }
    );

    wrapper.addEventListener('shopify:section:unload', () => {
      if (typeof mm !== 'undefined') {
        mm.revert();
      }
    });
  };

  const initCompareSections = (root = document) => {
    const wrappers = queryAll(
      root,
      '.muuva-vs-wrapper[data-section-id]'
    );

    wrappers.forEach(initCompare);
  };
  // ===== FIN SECCIÓN: combo-local_comparacion.liquid =====

  // ===== SECCIÓN: combo-local_faq.liquid =====
  /* ==========================================================================
     SECCIÓN FAQS & GARANTÍA (combo-local_faq.liquid) — Modo E (2.8)
     Referencia: Faq-Section_v1.txt (muuva-faq-guarantee v6.8.1)
     Módulos: initFaq (acordeón details único-abierto + GSAP opt-in) | initFaqSections
     ========================================================================== */

  const initFaq = (wrapper) => {
    if (!wrapper || initializedSections.has(wrapper)) {
      return;
    }

    initializedSections.add(wrapper);

    const detailsElements = queryAll(wrapper, 'details.muuva-faq-item-card');

    /* Apertura exclusiva: un solo acordeón abierto a la vez (data-single-open) */
    if (wrapper.dataset.singleOpen === 'true') {
      detailsElements.forEach((targetDetails) => {
        targetDetails.addEventListener('toggle', () => {
          if (targetDetails.open) {
            detailsElements.forEach((otherDetails) => {
              if (otherDetails !== targetDetails && otherDetails.open) {
                otherDetails.open = false;
              }
            });
          }
        });
      });
    }

    /* GSAP opt-in (5.13): entrada scoped con guard. CSS nativo si no hay GSAP. */
    if (wrapper.dataset.animate !== 'true' || typeof gsap === 'undefined') {
      return;
    }

    /* La referencia expone animation_duration en ms (150-500); GSAP consume segundos */
    const duration = (parseFloat(wrapper.dataset.duration) || 300) / 1000;
    const ease = wrapper.dataset.ease || 'power2.out';
    const animationType = wrapper.dataset.animationType || 'fade-up';
    const items = queryAll(wrapper, '.muuva-faq-header, .muuva-faq-item-card, .muuva-guarantee-card');

    if (!items.length) return;

    const mm = gsap.matchMedia();

    mm.add(
      {
        reduceMotion: '(prefers-reduced-motion: reduce)'
      },
      (context) => {
        if (context.conditions.reduceMotion) return;

        const from = {
          opacity: 0,
          duration: duration,
          ease: ease
        };

        if (animationType === 'fade-up') {
          from.y = 24;
        }

        gsap.from(items, from);
      }
    );

    wrapper.addEventListener('shopify:section:unload', () => {
      if (typeof mm !== 'undefined') {
        mm.revert();
      }
    });
  };

  const initFaqSections = (root = document) => {
    const wrappers = queryAll(
      root,
      '.muuva-faq-wrapper[data-section-id]'
    );

    wrappers.forEach(initFaq);
  };
  // ===== SECCIÓN: exp2_hero.liquid (Fase 7 V.1.1, aditivo) =====
  // ponytail: contador propio MAX 4 (2 sales + 2 ghees); clases exp2-* para no chocar con initBuilder de 3 ghees.
  const EXP2_MAX_SALT = 2;
  const EXP2_MAX_BUTTER = 2;
  const EXP2_MAX = 4;

  const initExp2Box = (builderBox) => {
    if (!builderBox || builderBox.dataset.exp2Bound) return;
    builderBox.dataset.exp2Bound = '1';
    const wrapper = builderBox.closest('.muuva-hero-wrapper') || document;
    const sectionId = getSectionId(wrapper);
    const esc = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape : (v) => String(v).replace(/[^a-zA-Z0-9-_]/g, '\\$&');
    // ===== CONTRATO: exp2 builder→resumen (Fase 14 V.1.1, aditivo) =====
    // ponytail: fija conexión constructor→resumen; lógica intacta, solo comentario.
    // Origen (chips): [data-exp2-kind="salt"|"butter"] + data-exp2-val + data-exp2-img (hero :634/:636/:679/:757)
    // Destino (resumen): [data-exp2-summary="salt-1/2|butter-1/2"] (:455/:480/:525/:550) via setSummary (:1530) + expandWithImg (:1523)
    // Destino (form): #exp2-salt-prop-1/2 + #exp2-butter-prop-1/2 (:1493-1494) + total #muuva-exp2-total (:1495) + submit (:1496)
    // Toggle/panel: [data-exp2-toggle] (:1497) + .muuva-exp2-panel (:1498), setOpen (:1568-1575) orden-independiente.
    const saltChips = Array.from(builderBox.querySelectorAll('[data-exp2-kind="salt"]'));
    const butterChips = Array.from(builderBox.querySelectorAll('[data-exp2-kind="butter"]'));
    if (!saltChips.length && !butterChips.length) return;
    const saltCounts = saltChips.map(() => 0);
    const butterCounts = butterChips.map(() => 0);
    if (saltCounts.length > 3) { saltCounts[0] = 1; saltCounts[3] = 1; }
    else if (saltCounts.length > 1) { saltCounts[0] = 1; saltCounts[1] = 1; }
    else if (saltCounts.length === 1) { saltCounts[0] = 2; }
    if (butterCounts.length > 2) { butterCounts[1] = 1; butterCounts[2] = 1; }
    else if (butterCounts.length > 1) { butterCounts[0] = 1; butterCounts[1] = 1; }
    else if (butterCounts.length === 1) { butterCounts[0] = 2; }

    const saltProps = [1, 2].map((n) => query(wrapper, `#exp2-salt-prop-${n}-${sectionId ? esc(sectionId) : ''}`));
    const butterProps = [1, 2].map((n) => query(wrapper, `#exp2-butter-prop-${n}-${sectionId ? esc(sectionId) : ''}`));
    const totalEl = query(builderBox, `#muuva-exp2-total-${sectionId ? esc(sectionId) : ''}`);
    const submitBtn = query(wrapper, `#muuva-exp2-submit-${sectionId ? esc(sectionId) : ''}`);
    const toggleBtn = query(builderBox, '[data-exp2-toggle]');
    const panel = query(builderBox, '.muuva-exp2-panel');
    const openText = (toggleBtn && toggleBtn.dataset.openText) || 'Personalizar Sabores';
    const closeText = (toggleBtn && toggleBtn.dataset.closeText) || 'Listo';

    const saltTotal = () => saltCounts.reduce((a, b) => a + b, 0);
    const butterTotal = () => butterCounts.reduce((a, b) => a + b, 0);
    const expand = (chips, counts) => {
      const out = [];
      chips.forEach((chip, i) => {
        for (let k = 0; k < counts[i]; k += 1) out.push(chip.dataset.exp2Val || '');
      });
      return out;
    };
    const paintChips = (chips, counts, groupTotal, groupMax) => {
      chips.forEach((chip, i) => {
        const c = counts[i];
        const countEl = query(chip, '.muuva-exp2-count');
        const minus = query(chip, '.muuva-exp2-minus');
        const plus = query(chip, '.muuva-exp2-plus');
        if (countEl && countEl.textContent !== String(c)) countEl.textContent = String(c);
        chip.classList.toggle('is-selected', c > 0);
        if (minus) minus.disabled = c <= 0;
        if (plus) plus.disabled = groupTotal >= groupMax;
      });
    };
    const expandWithImg = (chips, counts) => {
      const out = [];
      chips.forEach((chip, i) => {
        for (let k = 0; k < counts[i]; k += 1) out.push(chip.getAttribute('data-exp2-img') || '');
      });
      return out;
    };
    const setSummary = (key, values, imgs) => {
      for (let i = 0; i < 2; i += 1) {
        const el = query(builderBox, `[data-exp2-summary="${key}-${i + 1}"]`);
        if (!el) continue;
        el.textContent = values[i] || '—';
        // ponytail: sync foto resumen; sin img se conserva visual inicial
        const item = el.closest ? el.closest('.muuva-summary-item') : null;
        const img = item ? query(item, '.muuva-summary-item__image') : null;
        if (img && imgs && imgs[i]) {
          img.src = imgs[i];
          if (img.hasAttribute('srcset')) img.srcset = `${imgs[i]} 96w`;
          img.alt = values[i] || '';
        }
      }
    };

    const updateExp2 = () => {
      const salts = expand(saltChips, saltCounts);
      const butters = expand(butterChips, butterCounts);
      const total = salts.length + butters.length;
      saltProps.forEach((input, i) => { if (input && input.value !== (salts[i] || '')) input.value = salts[i] || ''; });
      butterProps.forEach((input, i) => { if (input && input.value !== (butters[i] || '')) input.value = butters[i] || ''; });
      paintChips(saltChips, saltCounts, salts.length, EXP2_MAX_SALT);
      paintChips(butterChips, butterCounts, butters.length, EXP2_MAX_BUTTER);
      setSummary('salt', salts, expandWithImg(saltChips, saltCounts));
      setSummary('butter', butters, expandWithImg(butterChips, butterCounts));
      if (totalEl) totalEl.textContent = total === EXP2_MAX ? '4/4 ✓' : `${total}/4`;
      const ready = total === EXP2_MAX;
      if (submitBtn) {
        submitBtn.style.opacity = ready ? '1' : '0.5';
        submitBtn.style.pointerEvents = ready ? 'auto' : 'none';
      }
      if (toggleBtn && panel) {
        const isOpen = !panel.hidden;
        toggleBtn.disabled = isOpen && !ready;
      }
    };

    const setOpen = (isOpen) => {
      if (!panel || !toggleBtn) return;
      panel.hidden = !isOpen;
      builderBox.classList.toggle('is-customizing', isOpen);
      toggleBtn.setAttribute('aria-expanded', String(isOpen));
      toggleBtn.textContent = isOpen ? closeText : openText;
      updateExp2();
    };

    if (toggleBtn && panel) {
      toggleBtn.addEventListener('click', () => setOpen(panel.hidden));
      setOpen(false);
    }
    builderBox.addEventListener('click', (event) => {
      const plus = event.target.closest('.muuva-exp2-plus');
      const minus = event.target.closest('.muuva-exp2-minus');
      const chip = event.target.closest('.muuva-exp2-chip');
      if (!chip || !builderBox.contains(chip)) return;
      const kind = chip.dataset.exp2Kind;
      const list = kind === 'salt' ? saltChips : butterChips;
      const counts = kind === 'salt' ? saltCounts : butterCounts;
      const idx = list.indexOf(chip);
      if (idx === -1) return;
      const groupMax = kind === 'salt' ? EXP2_MAX_SALT : EXP2_MAX_BUTTER;
      const groupTotal = counts.reduce((a, b) => a + b, 0);
      if (plus || (!minus && chip)) {
        if (groupTotal < groupMax && counts[idx] < groupMax) {
          event.preventDefault();
          counts[idx] += 1;
          updateExp2();
        }
        return;
      }
      if (minus && counts[idx] > 0) {
        event.preventDefault();
        counts[idx] -= 1;
        updateExp2();
      }
    }, { passive: false });
    updateExp2();
  };

  const initExp2Builder = (root) => {
    const scope = root || document;
    const boxes = [];
    if (scope.matches && scope.matches('[data-exp2-builder]')) boxes.push(scope);
    scope.querySelectorAll('[data-exp2-builder]').forEach((b) => boxes.push(b));
    if (!boxes.length) return;
    boxes.forEach(initExp2Box);
  };
  // ===== FIN SECCIÓN: exp2_hero.liquid =====

  // ===== SECCIÓN: exp2_accion sticky (Fase 6 V.1.1, aditivo) =====
  // ponytail: oculta sticky cuando el CTA principal está visible; sin observer no hace nada.
  const initActionSticky = (wrapper) => {
    if (!wrapper || wrapper.dataset.actionStickyBound) return;
    wrapper.dataset.actionStickyBound = '1';
    const section = wrapper.parentElement || document;
    const bar = (section.querySelector && section.querySelector('[data-sticky-bar]'))
      || document.querySelector('[data-sticky-bar]');
    if (!bar) return;
    const cta = wrapper.querySelector('.muuva-action-cta-box');
    if (!cta || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        bar.style.display = entry.isIntersecting ? 'none' : '';
      });
    }, { threshold: 0 });
    observer.observe(cta);
  };

  const initActionSections = (root = document) => {
    const wrappers = queryAll(
      root,
      '.muuva-action-wrapper[data-section-id]'
    );

    wrappers.forEach(initActionSticky);
  };
  // ===== FIN SECCIÓN: exp2_accion sticky =====

  /**
   * ------------------------------------------------------------------------
   * DESCUBRIR SECCIONES EXISTENTES
   * ------------------------------------------------------------------------
   */

  const initAllSections = (root = document) => {
    const wrappers = queryAll(
      root,
      SECTION_SELECTOR
    );

    wrappers.forEach(initSection);

    initTransformSections(root);

    initPgridSections(root);

    initProofSections(root);

    initCompareSections(root);

    initFaqSections(root);

    initActionSections(root);

    initExp2Builder(root);
  };

  window[GLOBAL_KEY] = { initAllSections };

  /**
   * ------------------------------------------------------------------------
   * CARGA INICIAL
   * ------------------------------------------------------------------------
   */

  initAllSections(document);

  /**
   * ------------------------------------------------------------------------
   * SHOPIFY THEME EDITOR
   * ------------------------------------------------------------------------
   */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const section = event.target;

      if (!section) return;

      initAllSections(section);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      /*
       * No necesitamos listeners globales adicionales.
       * El wrapper eliminado por Shopify desaparece junto con
       * sus listeners y WeakSet evita retener referencias.
       */
      void event;
    }
  );

})();
